import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getAdminAttendanceReport } from "../../api/attendanceApi";
import { getEmployeeDropdown } from "../../api/employeeApi";
import { getLeaveSummary } from "../../api/leaveApi";
import Loader from "../../components/Loader";
import SummaryCircle from "../../components/SummaryCircle";
import PremiumLeaveCard from "../../components/PremiumLeaveCard";
import { formatDateDMY } from "../../utils/dateFormatter";

const getTodayDate = () => {
  const today = new Date();
  return today.toISOString().slice(0, 10);
};

const getCurrentMonth = () => {
  const today = new Date();
  const year = today.getFullYear();
  const m = String(today.getMonth() + 1).padStart(2, "0");
  return `${year}-${m}`;
};

const AttendanceReport = () => {
  const navigate = useNavigate();

  const [viewMode, setViewMode] = useState("DAILY");
  const [month, setMonth] = useState(getCurrentMonth());
  const [employee, setEmployee] = useState("all");
  const [department, setDepartment] = useState("all");

  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);

  const [records, setRecords] = useState([]);
  const [leaveSummary, setLeaveSummary] = useState(null);

  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [date, setDate] = useState(getTodayDate());

  const [summaryCircles, setSummaryCircles] = useState({
    present: 0,
    holiday: 0,
    week_off: 0,
    absent: 0,
    paid_leave: 0,
    comp_off: 0,
    late_mark: 0,
    shortfall: 0,
    half_day: 0,
    paid_day: 0,
  });

  /* ================= HELPERS ================= */

  const formatTime = (value) => {
    if (!value) return "-";
    const d = new Date(value.replace(" ", "T"));
    if (isNaN(d.getTime())) return value;
    return d.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).toLowerCase();
  };

  const convertToHours = (timeStr) => {
    if (!timeStr) return 0;

    const h = timeStr.match(/(\d+)h/);
    const m = timeStr.match(/(\d+)m/);

    const hours = h ? parseInt(h[1]) : 0;
    const minutes = m ? parseInt(m[1]) : 0;

    return hours + minutes / 60;
  };

  const totalLeave = useMemo(() => {
    if (!leaveSummary) return 0;
    let sum = 0;
    Object.keys(leaveSummary).forEach((key) => {
      if (key.endsWith("_leave")) {
        sum += Number(leaveSummary[key] || 0);
      }
    });
    return sum;
  }, [leaveSummary]);

  const takenLeave = useMemo(() => {
    if (!leaveSummary) return 0;
    let sum = 0;
    Object.keys(leaveSummary).forEach((key) => {
      if (key.endsWith("_used")) {
        sum += Number(leaveSummary[key] || 0);
      }
    });
    return sum;
  }, [leaveSummary]);

  const balanceLeave = totalLeave - takenLeave;
  /* ================= LOAD EMPLOYEES ================= */

  useEffect(() => {
    getEmployeeDropdown().then((res) => {
      const data = res.data || [];
      setEmployees(data);

      const uniqueDepartments = [
        ...new Set(data.map((e) => e.department).filter(Boolean)),
      ];
      setDepartments(uniqueDepartments);
    });
  }, []);

  /* ================= LOAD ATTENDANCE ================= */

  useEffect(() => {
    const loadAttendance = async () => {
      try {
        if (viewMode === "MONTHLY" && !month) return;

        setLoading(true);

        const params = {
          employee,
          department,
          status: statusFilter,
          ...(viewMode === "DAILY" ? { date } : { month }),
        };

        const res = await getAdminAttendanceReport(params);
        setRecords(res.data.results || []);
        setSummaryCircles(
          res.data.attendance_summary || {
            present: 0,
            holiday: 0,
            week_off: 0,
            absent: 0,
            paid_leave: 0,
            comp_off: 0,
            late_mark: 0,
            shortfall: 0,
            half_day: 0,
            paid_day: 0,
          }
        );
      } catch (err) {
        console.error("API ERROR", err);
      } finally {
        setLoading(false);
      }
    };

    loadAttendance();
  }, [date, month, employee, department, statusFilter, viewMode]);

  /* ================= LOAD LEAVE SUMMARY ================= */
  // Only load leave summary when a specific employee is selected
  useEffect(() => {
    if (!employee || employee === "all") {
      setLeaveSummary(null);
      return;
    }
    getLeaveSummary(employee)
      .then((res) => setLeaveSummary(res.data))
      .catch(() => setLeaveSummary(null));
  }, [employee]);

  /* ================= FILTER ================= */

  const filteredRecords = useMemo(() => {
    let data = [...records];

    if (department !== "all") {
      data = data.filter((r) => r.department === department);
    }

    if (employee !== "all") {
      data = data.filter((r) => r.employee_email === employee);
    }

    if (viewMode === "MONTHLY" && month) {
      data = data.filter((r) => r.date.startsWith(month));
    }

    if (viewMode === "DAILY" && date) {
      data = data.filter((r) => r.date === date);
    }

    if (statusFilter !== "ALL") {
      data = data.filter((r) => r.status === statusFilter);
    }

    return data;
  }, [records, date, month, viewMode, department, employee, statusFilter]);

  /* ================= SUMMARY ================= */

  const summary = useMemo(() => {
    let present = 0;
    let absent = 0;
    let half = 0;
    let hours = 0;

    filteredRecords.forEach((r) => {
      if (r.status === "PRESENT") present++;
      else if (r.status === "ABSENT") absent++;
      else if (r.status === "HALF_DAY") half++;

      hours += convertToHours(r.working_hours);
    });

    return { present, absent, half, hours };
  }, [filteredRecords]);

  /* ================= EXPORT CSV ================= */

  const handleExportCSV = () => {
    if (filteredRecords.length === 0) {
      alert("No data available to export");
      return;
    }

    const headers = [
      "Name",
      "Employee Email",
      "Date",
      "Sign In",
      "Sign Out",
      "Hours",
      "Status",
    ];

    const rows = filteredRecords.map((r) => [
      `"${r.employee_name || ""}"`,
      `"${r.employee_email || ""}"`,
      `"${formatDateDMY(r.date) || ""}"`,
      `"${formatTime(r.sign_in).replace(/"/g, '""') || ""}"`,
      `"${formatTime(r.sign_out).replace(/"/g, '""') || ""}"`,
      `"${r.working_hours || ""}"`,
      `"${r.status || ""}"`,
    ]);

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers.join(","), ...rows.map((row) => row.join(","))].join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute(
      "download",
      `Attendance_Report_${viewMode === "DAILY" ? date : month}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-8">
      {/* ================= HEADER & VIEW MODE ================= */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-extrabold text-slate-800 tracking-tight">
            Attendance Report (Admin)
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Monitor, filter, and export employee attendance metrics.
          </p>
        </div>

        <div className="flex items-center bg-slate-100 p-1 rounded-full w-fit">
          <button
            onClick={() => setViewMode("DAILY")}
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
              viewMode === "DAILY"
                ? "bg-white text-blue-600 shadow-sm"
                : "text-slate-600 hover:text-slate-800"
            }`}
          >
            Daily View
          </button>

          <button
            onClick={() => setViewMode("MONTHLY")}
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
              viewMode === "MONTHLY"
                ? "bg-white text-blue-600 shadow-sm"
                : "text-slate-600 hover:text-slate-800"
            }`}
          >
            Monthly View
          </button>

          <button
            onClick={() => navigate("/admin/leave-balance")}
            className="ml-2 px-5 py-2 rounded-full text-sm font-semibold bg-violet-600 hover:bg-violet-700 text-white transition-colors duration-200 shadow-sm"
          >
            Leave Balance
          </button>
        </div>
      </div>

      {/* ================= FILTERS ================= */}
      <div className="bg-white border border-slate-100 p-6 rounded-2xl shadow-sm grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
        {viewMode === "DAILY" && (
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full border border-slate-200 px-3 py-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm transition-all"
            />
          </div>
        )}

        {viewMode === "MONTHLY" && (
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Month</label>
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="w-full border border-slate-200 px-3 py-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm transition-all"
            />
          </div>
        )}

        <div>
          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Department</label>
          <select
            value={department}
            onChange={(e) => setDepartment(e.target.value)}
            className="w-full border border-slate-200 px-3 py-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm bg-white transition-all"
          >
            <option value="all">All Departments</option>
            {departments.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Employee</label>
          <select
            value={employee}
            onChange={(e) => setEmployee(e.target.value)}
            className="w-full border border-slate-200 px-3 py-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm bg-white transition-all"
          >
            <option value="all">All Employees</option>
            {employees.map((e) => (
              <option key={e.email} value={e.email}>
                {e.email}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full border border-slate-200 px-3 py-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm bg-white transition-all"
          >
            <option value="ALL">All Status</option>
            <option value="PRESENT">Present</option>
            <option value="ABSENT">Absent</option>
            <option value="HALF_DAY">Half Day</option>
          </select>
        </div>
      </div>

     

      {/* ================= SUMMARY CIRCLES ================= */}
      {loading ? (
        <div className="bg-white rounded-2xl shadow-sm p-6 border border-slate-100 animate-pulse">
          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-10 gap-6 text-center justify-center">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="flex flex-col items-center">
                <div className="w-16 h-16 bg-slate-100 rounded-full"></div>
                <div className="h-3 w-16 bg-slate-100 rounded mt-3"></div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        summaryCircles && (
          <div className="bg-white rounded-2xl shadow-sm p-6 border border-slate-100">
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-10 gap-6 text-center justify-center">
              <SummaryCircle label="Present" value={summaryCircles.present ?? 0} color="green" total={30} />
              <SummaryCircle label="Holiday" value={summaryCircles.holiday ?? 0} color="purple" total={30} />
              <SummaryCircle label="Week Off" value={summaryCircles.week_off ?? 0} color="blue" total={30} />
              <SummaryCircle label="Paid Leave" value={summaryCircles.paid_leave ?? 0} color="orange" total={30} />
              <SummaryCircle label="Comp Off" value={summaryCircles.comp_off ?? 0} color="orange" total={30} />
              <SummaryCircle label="Absent" value={summaryCircles.absent ?? 0} color="red" total={30} />
              <SummaryCircle label="Late Mark" value={summaryCircles.late_mark ?? 0} color="pink" total={30} />
              <SummaryCircle label="Shortfall" value={summaryCircles.shortfall ?? 0} color="pink" total={30} />
              <SummaryCircle label="Half Day" value={summaryCircles.half_day ?? 0} color="gray" total={30} />
              <SummaryCircle label="Paid Day" value={summaryCircles.paid_day ?? 0} color="purple" total={30} />
            </div>
          </div>
        )
      )}
      

      {/* ================= LEAVE CARDS (only when specific employee is selected) ================= */}
      {employee && employee !== "all" && leaveSummary && (
        <div className="space-y-4">
          <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider">Leave Balance</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Summary totals */}
            <PremiumLeaveCard title="Total Leave" value={totalLeave} color="total" simple />
            <PremiumLeaveCard title="Leave Taken" value={takenLeave} color="taken" simple />
            <PremiumLeaveCard title="Balance Leave" value={balanceLeave} color="balance" simple />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Per leave-type cards: Used / Allocated */}
            {Object.keys(leaveSummary)
              .filter((key) => key.endsWith("_leave"))
              .map((key) => {
                const baseKey = key.slice(0, -6);
                const allowed = Number(leaveSummary[key] || 0);
                const used = Number(leaveSummary[`${baseKey}_used`] || 0);
                const displayTitle =
                  baseKey
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (char) => char.toUpperCase()) + " Leave";

                let colorType = "default";
                const lowerTitle = displayTitle.toLowerCase();
                if (lowerTitle.includes("casual")) colorType = "casual";
                else if (lowerTitle.includes("sick")) colorType = "sick";
                else if (lowerTitle.includes("compensatory") || lowerTitle.includes("comp"))
                  colorType = "compensatory";
                else if (lowerTitle.includes("loss") || lowerTitle.includes("lop"))
                  colorType = "taken";

                return (
                  <PremiumLeaveCard
                    key={key}
                    title={displayTitle}
                    allowed={allowed}
                    used={used}
                    color={colorType}
                  />
                );
              })}
          </div>
        </div>
      )}

      

 {/* ================= EXPORT CSV BUTTON AREA ================= */}
      <div className="border-t border-b border-slate-100 py-4 flex justify-end
      ">
        <button
          onClick={handleExportCSV}
          className="px-6 py-2.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm shadow hover:shadow-md transition-all duration-200 flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Export CSV
        </button>
      </div>
      {/* ================= TABLE ================= */}
      {loading ? (
        <Loader />
      ) : (
        <div className="bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto max-h-[600px] scrollbar-thin">
            <table className="min-w-full text-sm text-left border-collapse">
              <thead className="sticky top-0 bg-slate-50 border-b border-slate-200/80 z-10">
                <tr>
                  <th className="py-4 px-5 font-bold text-slate-500 text-xs uppercase tracking-wider">Employee</th>
                  <th className="py-4 px-5 font-bold text-slate-500 text-xs uppercase tracking-wider">Email</th>
                  <th className="py-4 px-5 font-bold text-slate-500 text-xs uppercase tracking-wider">Date</th>
                  <th className="py-4 px-5 font-bold text-slate-500 text-xs uppercase tracking-wider">Sign In</th>
                  <th className="py-4 px-5 font-bold text-slate-500 text-xs uppercase tracking-wider">Sign Out</th>
                  <th className="py-4 px-5 font-bold text-slate-500 text-xs uppercase tracking-wider">Hours</th>
                  <th className="py-4 px-5 font-bold text-slate-500 text-xs uppercase tracking-wider text-center">Status</th>
                  <th className="py-4 px-5 font-bold text-slate-500 text-xs uppercase tracking-wider text-center">Flags</th>
                  <th className="py-4 px-5 font-bold text-slate-500 text-xs uppercase tracking-wider">Holiday Name</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredRecords.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="py-8 px-5 text-center text-slate-400 font-medium">
                      No records found
                    </td>
                  </tr>
                ) : (
                  filteredRecords.map((r) => (
                    <tr
                      className={`transition-colors hover:bg-slate-50/50 ${
                        r.status === "ABSENT"
                          ? "bg-rose-50/20 hover:bg-rose-50/40"
                          : r.is_late || r.is_shortfall
                          ? "bg-amber-50/20 hover:bg-amber-50/40"
                          : ""
                      }`}
                      key={r.id || `${r.employee_email}-${r.date}`}
                    >
                      <td className="py-4 px-5 font-semibold text-slate-700">{r.employee_name}</td>
                      <td className="py-4 px-5 text-slate-500">{r.employee_email}</td>
                      <td className="py-4 px-5 text-slate-600 font-medium">{formatDateDMY(r.date)}</td>
                      <td className="py-4 px-5 text-slate-700">
                        {formatTime(r.sign_in)}
                       
                      </td>
                      <td className="py-4 px-5 text-slate-700">
                        {formatTime(r.sign_out)}
                        {(r.is_auto_signout || r.auto_signout_flag) && (
                          <div className="mt-1 text-xs font-semibold text-red-600">
                            🚩 Auto Sign-Out
                          </div>
                        )}
                        {r.is_shortfall && (
                          <div className="mt-1 text-xs font-semibold text-amber-600" title="Worked less than 8h 30m">
                            ⏱ Shortfall
                          </div>
                        )}
                      </td>
                      <td className="py-4 px-5 font-semibold text-slate-700">{r.working_hours}</td>
                      <td className="py-4 px-5 text-center">
                        <StatusBadge status={r.status} />
                      </td>
                      {/* Flags column */}
                      <td className="py-4 px-5 text-center">
                        <div className="flex flex-wrap gap-1 justify-center">
                          {r.is_late && (
                            <span className="px-2.5 py-0.5 text-orange-700 text-[10px] font-bold uppercase tracking-wider">
                              Late Mark
                            </span>
                          )}
                          {r.is_shortfall && (
                            <span className="px-2.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] rounded-full font-bold uppercase tracking-wider">
                              Shortfall
                            </span>
                          )}
                          {!r.is_late && !r.is_shortfall && (
                            <span className="text-slate-300 text-xs">—</span>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-5 font-semibold text-purple-600">
                        {r.holiday_name || "-"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

/* ================= REUSABLE ================= */

const StatusBadge = ({ status }) => {
  const map = {
    PRESENT: { bg: "bg-emerald-50 text-emerald-700 border border-emerald-200/60", dot: "bg-emerald-500" },
    ABSENT: { bg: "bg-rose-50 text-rose-700 border border-rose-200/60", dot: "bg-rose-500" },
    HALF_DAY: { bg: "bg-amber-50 text-amber-700 border border-amber-200/60", dot: "bg-amber-500" },
    WEEK_OFF: { bg: "bg-slate-50 text-slate-600 border border-slate-200/60", dot: "bg-slate-400" },
    HOLIDAY: { bg: "bg-violet-50 text-violet-700 border border-violet-200/60", dot: "bg-violet-500" },
    COMPENSATORY_OFF: { bg: "bg-sky-50 text-sky-700 border border-sky-200/60", dot: "bg-sky-500" },
    PAID_LEAVE: { bg: "bg-teal-50 text-teal-700 border border-teal-200/60", dot: "bg-teal-500" },
  };
  const config = map[status] || { bg: "bg-slate-50 text-slate-600 border border-slate-200/60", dot: "bg-slate-400" };
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 ${config.bg}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`}></span>
      {status.replace(/_/g, " ")}
    </span>
  );
};

export default AttendanceReport;